import { sampleCo2ForKm, sampleMetrics } from './sample-company';
import type { SustainabilitySavesResponse } from 'src/app/models/sustainability.interface';
import type {
  IncentiveChannel,
  IncentiveSegment,
  IncentivesCampaignGroup,
  IncentivesCampaignRecipient,
  IncentivesCampaignStatus,
  IncentivesChildCampaign,
  IncentivesRecipientCancelReason,
  IncentivesRecipientStatus,
  IncentivesSummary,
} from 'src/app/models/incentives-api.interface';
import type { CorporationSubsidyPricingConfig } from 'src/app/models/corporation-subsidy-pricing-config.interface';
import type { CorporationOfficeSubsidyPricingConfig } from 'src/app/models/corporation-office-subsidy-pricing-config.interface';

/**
 * Cifras de carpooling de «WAIIS S.L.» para Sostenibilidad, Incentivos y
 * Subvención (dashboard de ejemplo y «Ver con datos de ejemplo»). Salen de
 * `sampleMetrics`, como el resto de pantallas, para que cuadren con Inicio,
 * Uso, Funnel y Empleados con el mismo periodo y la misma sede.
 */

const DEMO_CORPORATION_ID = 'ejemplo';
const MADRID = 'demo-office-madrid';
const GETAFE = 'demo-office-getafe';

// ─── Sostenibilidad ──────────────────────────────────────────────────────────

/** kg de CO₂ que absorbe un árbol: la equivalencia del back (`CorporationsCarbonFootprintSavesService`). */
export const SAMPLE_KG_CO2_PER_TREE = 1.75;

/**
 * La empresa empezó el año con menos gente compartiendo coche: cada mes antes
 * del último cerrado pesa un 4 % menos (sin bajar del 60 %).
 */
const MONTHLY_RAMP = 0.04;
const MIN_RAMP = 0.6;

export interface SampleSustainabilityMonth {
  year: number;
  /** 1 (enero) … 12. */
  month: number;
  co2Kg: number;
  trees: number;
  /** Km recorridos con pasajeros: los que el back llama «evitados». */
  km: number;
  /** Personas por coche compartido: quien conduce y sus pasajeros. */
  occupants: number;
}

export interface SampleSustainabilityQuery {
  officeId?: string | null;
  year: number;
  month: number;
  /** Cuándo se mira: fija cuál es el último mes cerrado. */
  now: Date;
}

/**
 * Un mes de Sostenibilidad. El último mes cerrado es exactamente lo que dicen
 * Inicio y Uso para esos días (`sampleMetrics` con los días del mes); los
 * anteriores, algo menos.
 */
export function sampleSustainabilityMonth({ officeId, year, month, now }: SampleSustainabilityQuery): SampleSustainabilityMonth {
  const days = new Date(year, month, 0).getDate();
  const m = sampleMetrics({ officeId, days });
  const monthsBefore = monthIndex(now.getFullYear(), now.getMonth()) - 1 - monthIndex(year, month - 1);
  const factor = Math.max(MIN_RAMP, 1 - MONTHLY_RAMP * Math.max(0, monthsBefore));
  const km = Math.round((m.kmWithPassengers * factor) / 10) * 10;
  const co2Kg = sampleCo2ForKm(km);
  return {
    year,
    month,
    co2Kg,
    trees: Math.round(co2Kg / SAMPLE_KG_CO2_PER_TREE),
    km,
    occupants: Math.round((1 + m.passengersPerTrip) * 10) / 10,
  };
}

/**
 * Respuesta del back de Sostenibilidad (`corporations-carbon-footprint-saves`)
 * para el mes que empieza en `dateStart` (`Y-m-d`): el mes, el acumulado del
 * año hasta él y las equivalencias, con las fórmulas del back.
 */
export function sampleCarbonFootprintSaves({
  officeId,
  dateStart,
  now,
}: {
  officeId?: string | null;
  dateStart: string;
  now: Date;
}): SustainabilitySavesResponse {
  const [year, month] = dateStart.split('-').map(Number);
  const months = Array.from({ length: month }, (_, i) => sampleSustainabilityMonth({ officeId, year, month: i + 1, now }));
  const current = months[months.length - 1];
  const co2 = current.co2Kg;
  return {
    total_saved_co2_kg: co2,
    annual_saved_co2_kg: months.reduce((sum, m) => sum + m.co2Kg, 0),
    avoided_km: current.km,
    avg_occupants: current.occupants,
    equivalents: {
      trees: current.trees,
      flights: Math.round(co2 / 255),
      led_hours: Math.round(co2 / 0.004),
      mobile_charges: Math.round(co2 / 0.006),
    },
  };
}

function monthIndex(year: number, monthZeroBased: number): number {
  return year * 12 + monthZeroBased;
}

// ─── Incentivos ──────────────────────────────────────────────────────────────

/** Días sin viajar para contar como inactivo (lo que dice el resumen del back). */
export const SAMPLE_INACTIVITY_DAYS = 7;

/** Empleados de `assets/demo/employees.json` que reciben campañas: número, nombre del correo y sede. */
const PEOPLE: Record<number, { name: string; officeId: string; hasAccount: boolean }> = {
  1: { name: 'lucia', officeId: MADRID, hasAccount: true },
  2: { name: 'hugo', officeId: GETAFE, hasAccount: true },
  4: { name: 'pablo', officeId: GETAFE, hasAccount: false },
  5: { name: 'sofia', officeId: MADRID, hasAccount: true },
  6: { name: 'daniel', officeId: GETAFE, hasAccount: true },
  7: { name: 'valeria', officeId: MADRID, hasAccount: true },
  8: { name: 'alvaro', officeId: GETAFE, hasAccount: true },
  9: { name: 'paula', officeId: MADRID, hasAccount: false },
  10: { name: 'adrian', officeId: GETAFE, hasAccount: true },
  11: { name: 'carmen', officeId: MADRID, hasAccount: true },
  12: { name: 'javier', officeId: MADRID, hasAccount: true },
  13: { name: 'elena', officeId: MADRID, hasAccount: true },
  14: { name: 'mario', officeId: MADRID, hasAccount: true },
  15: { name: 'lola', officeId: MADRID, hasAccount: true },
  16: { name: 'diego', officeId: MADRID, hasAccount: true },
  17: { name: 'irene', officeId: MADRID, hasAccount: true },
  18: { name: 'raul', officeId: MADRID, hasAccount: true },
  19: { name: 'nuria', officeId: MADRID, hasAccount: true },
  20: { name: 'sergio', officeId: MADRID, hasAccount: false },
  21: { name: 'andrea', officeId: GETAFE, hasAccount: true },
  22: { name: 'ivan', officeId: GETAFE, hasAccount: true },
  23: { name: 'clara', officeId: GETAFE, hasAccount: false },
};

/** Un destinatario: empleado, estado y hace cuántos días acabó (pagado o cancelado). */
type RecipientDef = [user: number, status: IncentivesRecipientStatus, daysAgo?: number, reason?: IncentivesRecipientCancelReason];

interface CampaignDef {
  id: string;
  segment: IncentiveSegment;
  channel: IncentiveChannel;
  /** Céntimos por empleado (solo con saldo). */
  amount: number | null;
  status: IncentivesCampaignStatus;
  recipients: RecipientDef[];
}

interface GroupDef {
  id: string;
  /** Hace cuántos días se lanzó. */
  daysAgo: number;
  /** Hace cuántos días se cerró (solo las terminadas). */
  closedDaysAgo?: number;
  campaigns: CampaignDef[];
}

/**
 * Tres lanzamientos, el más reciente primero: la reactivación de los
 * inactivos, la bienvenida (correo a quien no se ha registrado y 5 € a quien
 * aún no ha viajado) y un «primer viaje» ya terminado, del que salieron varios
 * de los que más comparten coche en Uso.
 */
const GROUPS: GroupDef[] = [
  {
    id: 'demo-incentive-group-reactivation',
    daysAgo: 5,
    campaigns: [
      {
        id: 'demo-incentive-reactivation-inactive',
        segment: 'inactive',
        channel: 'credit',
        amount: 300,
        status: 'in_progress',
        recipients: [[5, 'completed', 2], [18, 'pending'], [19, 'pending'], [22, 'pending']],
      },
    ],
  },
  {
    id: 'demo-incentive-group-welcome',
    daysAgo: 12,
    campaigns: [
      {
        id: 'demo-incentive-welcome-not-registered',
        segment: 'not_registered',
        channel: 'email',
        amount: null,
        status: 'in_progress',
        recipients: [[4, 'pending'], [9, 'pending'], [23, 'pending']],
      },
      {
        id: 'demo-incentive-welcome-no-trips',
        segment: 'no_trips',
        channel: 'credit',
        amount: 500,
        status: 'in_progress',
        recipients: [
          [11, 'completed', 9],
          [12, 'completed', 6],
          [6, 'pending'],
          [13, 'pending'],
          [14, 'pending'],
          [15, 'pending'],
          [16, 'pending'],
          [17, 'pending'],
          [21, 'pending'],
        ],
      },
    ],
  },
  {
    id: 'demo-incentive-group-first-trip',
    daysAgo: 70,
    closedDaysAgo: 40,
    campaigns: [
      {
        id: 'demo-incentive-first-trip-no-trips',
        segment: 'no_trips',
        channel: 'credit',
        amount: 500,
        status: 'completed',
        recipients: [
          [1, 'completed', 66],
          [2, 'completed', 65],
          [7, 'completed', 62],
          [8, 'completed', 60],
          [10, 'completed', 57],
          [20, 'cancelled', 61, 'employee_unlinked'],
        ],
      },
    ],
  },
];

const LIVE: IncentivesCampaignStatus[] = ['scheduled', 'in_progress'];

/**
 * Resumen de Incentivos para una sede (o toda la empresa), del funnel de
 * `sampleMetrics`: sin registrar = plantilla − registrados; sin verificar el
 * DNI = registrados − vinculados; sin viajes = vinculados − con algún viaje;
 * inactivos = con algún viaje − activos en los últimos 7 días. De cada uno,
 * «en curso» son los pendientes de las campañas vivas y el resto, a quien
 * llegaría una campaña nueva.
 */
export function sampleIncentivesSummary(officeId?: string | null): IncentivesSummary {
  const m = sampleMetrics({ officeId });
  const tripCompleted = m.funnel[2];
  const totals: Record<IncentiveSegment, number> = {
    not_registered: m.declared - m.registered,
    pending_dni_verification: m.registered - m.linked,
    no_trips: m.linked - tripCompleted,
    inactive: Math.max(0, tripCompleted - m.activeWeek),
  };
  const inProgress = (segment: IncentiveSegment) =>
    GROUPS.flatMap((g) => g.campaigns)
      .filter((c) => c.segment === segment && LIVE.includes(c.status))
      .flatMap((c) => recipientsIn(c, officeId))
      .filter(([, status]) => status === 'pending').length;
  const segment = (key: IncentiveSegment) => {
    const in_progress = inProgress(key);
    return { eligible: totals[key] - in_progress, in_progress };
  };
  return {
    not_registered: segment('not_registered'),
    pending_dni_verification: segment('pending_dni_verification'),
    no_trips: segment('no_trips'),
    inactive: segment('inactive'),
    inactivity_threshold_days: SAMPLE_INACTIVITY_DAYS,
  };
}

/**
 * Grupos de campañas, el más reciente primero. Con una sede, cada campaña
 * cuenta solo a los empleados de esa sede (y desaparece si no le queda
 * ninguno), como el resto de pantallas.
 */
export function sampleIncentiveGroups(officeId: string | null | undefined, now: Date): IncentivesCampaignGroup[] {
  return GROUPS.map((g) => buildGroup(g, officeId, now)).filter((g) => g.campaigns.length > 0);
}

/** Destinatarios de una campaña (de la sede, si se pide una). */
export function sampleIncentiveRecipients(
  campaignId: string,
  officeId: string | null | undefined,
  now: Date,
): IncentivesCampaignRecipient[] {
  for (const g of GROUPS) {
    const c = g.campaigns.find((campaign) => campaign.id === campaignId);
    if (c) return recipientsIn(c, officeId).map((r) => buildRecipient(c, r, now));
  }
  return [];
}

function recipientsIn(c: CampaignDef, officeId: string | null | undefined): RecipientDef[] {
  return c.recipients.filter(([user]) => !officeId || PEOPLE[user].officeId === officeId);
}

function buildRecipient(c: CampaignDef, [user, status, daysAgo, reason]: RecipientDef, now: Date): IncentivesCampaignRecipient {
  const person = PEOPLE[user];
  const when = daysAgo == null ? null : isoDaysAgo(now, daysAgo);
  return {
    id: `${c.id}-user-${user}`,
    incentive_campaign_id: c.id,
    corporation_user_id: `demo-user-${user}`,
    user_id: person.hasAccount ? `demo-app-user-${user}` : null,
    recipient_email: `${person.name}.${user}@ejemplo.es`,
    segment: c.segment,
    amount: c.channel === 'credit' ? c.amount : null,
    status,
    completed_at: status === 'completed' ? when : null,
    cancelled_at: status === 'cancelled' ? when : null,
    cancel_reason: status === 'cancelled' ? reason ?? 'manual' : null,
  };
}

function buildGroup(g: GroupDef, officeId: string | null | undefined, now: Date): IncentivesCampaignGroup {
  const createdAt = isoDaysAgo(now, g.daysAgo);
  const closedAt = g.closedDaysAgo == null ? null : isoDaysAgo(now, g.closedDaysAgo);
  const campaigns: IncentivesChildCampaign[] = g.campaigns
    .map((c) => ({ c, rows: recipientsIn(c, officeId) }))
    .filter(({ rows }) => rows.length > 0)
    .map(({ c, rows }) => {
      const by = (status: IncentivesRecipientStatus) => rows.filter(([, s]) => s === status).length;
      const unitCost = c.channel === 'credit' ? c.amount ?? 0 : 0;
      return {
        id: c.id,
        incentive_campaign_group_id: g.id,
        corporation_id: DEMO_CORPORATION_ID,
        corporation_office_id: null,
        created_by: null,
        status: c.status,
        segment: c.segment,
        channel: c.channel,
        amount: c.amount,
        send_at: null,
        sent_at: createdAt,
        charged_amount: 0,
        total_recipients: rows.length,
        completed_recipients: by('completed'),
        retained_amount: unitCost * (rows.length - by('cancelled')),
        inactivity_threshold_days: c.segment === 'inactive' ? SAMPLE_INACTIVITY_DAYS : null,
        failure_reason: null,
        completed_at: c.status === 'completed' ? closedAt : null,
        cancelled_at: null,
        cancelled_by: null,
        created_at: createdAt,
        items: [],
        recipients_by_status: { pending: by('pending'), completed: by('completed'), cancelled: by('cancelled') },
      };
    });
  const total = (pick: (c: IncentivesChildCampaign) => number) => campaigns.reduce((sum, c) => sum + pick(c), 0);
  return {
    id: g.id,
    corporation_id: DEMO_CORPORATION_ID,
    corporation_office_id: null,
    created_by: null,
    // Como el back: vivo mientras quede una hija viva.
    status: campaigns.some((c) => LIVE.includes(c.status)) ? 'in_progress' : 'completed',
    send_at: null,
    retained_amount: total((c) => c.retained_amount),
    total_recipients: total((c) => c.total_recipients ?? 0),
    completed_recipients: total((c) => c.completed_recipients ?? 0),
    created_at: createdAt,
    campaigns,
  };
}

/** Fecha ISO de hace `days` días, a las 9:30 (hora a la que se lanzan y pagan en el ejemplo). */
function isoDaysAgo(now: Date, days: number): string {
  const d = new Date(now.getTime() - days * 86_400_000);
  d.setUTCHours(8, 30, 0, 0);
  return d.toISOString();
}

// ─── Subvención ──────────────────────────────────────────────────────────────

const SUBSIDY_CREATED_AT = '2026-02-01T09:00:00Z';

/**
 * Bonificación por viaje de «WAIIS S.L.» (`corporation-subsidy-pricing-configs`,
 * lista suelta, en céntimos): 1 € por viaje compartido para toda la empresa.
 */
export function sampleSubsidyConfigs(): CorporationSubsidyPricingConfig[] {
  return [
    {
      id: 'demo-subsidy-config',
      corporation_id: DEMO_CORPORATION_ID,
      office_id: null,
      subsidy_type: 'fixed',
      subsidy_value: 100,
      waiis_contributes: false,
      waiis_contribution_type: null,
      waiis_contribution_value: null,
      final_price_zero: false,
      applies_to_employees_only: true,
      limit_type: 'none',
      limit_date: null,
      limit_amount: null,
      consumed_amount: 0,
      base_price_recipient: 'driver',
      active: true,
      refund_credits: false,
      created_at: SUBSIDY_CREATED_AT,
      updated_at: SUBSIDY_CREATED_AT,
    },
  ];
}

/**
 * Bonificación de cada sede (`corporation-office-subsidy-pricing-configs`, en
 * céntimos): Madrid paga algo más por viaje que Getafe.
 */
export function sampleOfficeSubsidyConfigs(): CorporationOfficeSubsidyPricingConfig[] {
  const office = (officeId: string, cents: number): CorporationOfficeSubsidyPricingConfig => ({
    id: `demo-office-subsidy-${officeId.replace('demo-office-', '')}`,
    corporation_id: DEMO_CORPORATION_ID,
    corporation_office_id: officeId,
    subsidy_discount: cents,
    active: true,
    blocked_at: null,
    blocked_reason: null,
    created_at: SUBSIDY_CREATED_AT,
    updated_at: SUBSIDY_CREATED_AT,
  });
  return [office(MADRID, 150), office(GETAFE, 100)];
}
